import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Option } from 'react-dropdown';
import { useDispatch, useSelector } from 'react-redux';
import { AnimalType } from 'shared/api/api';
import { GetSyncData } from 'shared/state/ducks/process-equipments/operations';
import { WebAppState } from 'web/state/store.web';
import { SkioldFormDropdown } from '../skiold-components/skiold-dropdown/skiold-form-dropdown';
import { SkioldDropdown } from '../skiold-components/skiold-dropdown/skiold-dropdown';

interface PropsFromParent {
	animalType?: AnimalType;
	penId?: string;
	sectionId?: string;
	onSelect?: (recipeNumber: number) => void;
	form?: boolean;
	useInTable?: boolean;
	className?: string;
	containerClassName?: string;
	theme?: 'light' | 'dark';
	selectedCurveNumber?: number | undefined;
}
export const DistriwinFeedCurvePicker: FC<PropsFromParent> = React.memo(
	({
		onSelect,
		animalType,
		penId,
		sectionId,
		form,
		useInTable,
		className,
		containerClassName,
		theme = 'dark',
		selectedCurveNumber,
	}) => {
		const [selected, setSelected] = useState<Option | undefined>(undefined);

		const ped = useSelector((state: WebAppState) =>
			state.processEquipmentData.entities.find(ped => ped.sectionId === sectionId && !ped.isFromImport),
		);

		const pe = useSelector((state: WebAppState) =>
			state.processEquipments.entities.find(pe => pe.code === ped?.code?.split(':')[0]),
		);

		const utps = useSelector((state: WebAppState) => state.unitToPenData.data);
		const valveGroups = useSelector((state: WebAppState) => state.adjustFeed.valveGroups);

		const utpsFiltered = useMemo(() => {
			return utps.filter(utp => utp.penId === penId);
		}, [utps, penId]);

		const valveGroupsFiltered = useMemo(() => {
			return valveGroups.filter(vg => utpsFiltered.map(x => x.unitGroupId).includes(vg.id));
		}, [utpsFiltered]);

		// Select animal indicator to show correct curves
		const animalIndicator = useMemo(() => {
			if (
				animalType === AnimalType.FRATS ||
				animalType === AnimalType.Finisher ||
				animalType === AnimalType.Weaner
			) {
				return 'pig';
			} else if (
				animalType === AnimalType.Sow ||
				animalType === AnimalType.Piglet ||
				animalType === AnimalType.YoungFemale
			) {
				return 'sow';
			} else {
				return 'pig';
			}
		}, [animalType]);

		const dispatch = useDispatch();
		const feedCurveOptions = useMemo(() => {
			// If only 1 ValveGroup is selected, show only the available FeedingCurves for that ValveGroup
			if (valveGroupsFiltered && valveGroupsFiltered.length === 1) {
				return valveGroupsFiltered[0]?.availableFeedingCurves
					?.filter(z => z.type === animalIndicator)
					?.map(
						x =>
							({
								value: x.curveId.toString(),
								label: x.name,
							} as Option),
					);
			} else if (pe) {
				// Else show all FeedingCurves for the ProcessEquipment
				return pe?.feedCurves
					?.filter(z => z.type === animalIndicator)
					?.map(
						x =>
							({
								value: x.curveId.toString(),
								label: x.name,
							} as Option),
					);
			}
			return [];
		}, [valveGroupsFiltered, pe, animalIndicator]);

		useEffect(() => {
			dispatch(GetSyncData());
		}, []);

		useEffect(() => {
			setSelected(feedCurveOptions?.find(x => x.value === selectedCurveNumber?.toString()));
		}, [selectedCurveNumber]);

		// Set recipe on init
		useEffect(() => {
			// Only show Curve, if 1 or more Valves have the same Curve
			if (valveGroupsFiltered && feedCurveOptions) {				
				const sets = [...new Set(valveGroupsFiltered.map(x => x.feedingCurve))];
				if (sets && sets.length === 1) {
					const item = feedCurveOptions.find(r => r.value === sets[0]?.toString());
					if (item) {
						setSelected(item);
						if (onSelect) {
							onSelect(+item.value);
						}
					}
				}
			}
		}, [valveGroupsFiltered, feedCurveOptions]);

		const onSelectLocal = useCallback(
			(newValue: Option, itemFromParent?: any) => {
				setSelected(newValue);
				if (onSelect) {
					onSelect(+newValue.value);
				}
			},
			[setSelected],
		);
		return form ? (
			<SkioldFormDropdown
				items={feedCurveOptions}
				theme={'dark'}
				containerClassName="grade-dropdown"
				selectedValue={selected}
				onValueChanged={onSelectLocal}
			/>
		) : useInTable ? (
			<SkioldDropdown
				items={feedCurveOptions}
				theme={theme}
				containerClassName={containerClassName}
				selectedValue={selected}
				onValueChanged={onSelectLocal}
				usedInTable={useInTable}
				className={className}
			/>
		) : (
			<SkioldDropdown
				items={feedCurveOptions}
				theme={'light'}
				containerClassName={`${containerClassName} basic-dropdown`}
				selectedValue={selected}
				onValueChanged={onSelectLocal}
			/>
		);
	},
);
